<template>
  <div>
    <div v-if="rowData.length > 0">
      <v-row
        v-for="(row, index) in rowData" 
        v-bind:key="index"
      >
        <v-col 
          v-if="row.inputs.length > 1 && !row.fallback"
          cols="12"
        >
          <v-alert
            dense 
            outlined 
            type="error"
            class="ma-0"
          >
            All encoders for an output must be from a single input! This output will not stream properly.
          </v-alert>
        </v-col>
        <v-col cols="4">
          <v-card
            outlined
            v-for="(input, inputIndex) in row.inputs" 
            v-bind:key="inputIndex"
            class="my-1"
            :class="input.video_input_format == 'FORMAT_UNDEFINED' ? errorColor() : ''"
          >
            <v-card-title class="text-subtitle-2 py-2">
              {{ inputName(input) }} 
              <v-spacer />
              <v-chip v-if="graphicOverlay(input)" x-small color="secondary" class="mx-1">{{ graphicOverlay(input) }}</v-chip>
              <v-chip v-if="input.type" x-small>{{ typeName(input.type) }}</v-chip>
            </v-card-title>
            <v-card-text class="my-0 pb-3">
              <rt-metrics
                v-if="device.isOnline() && device.hasFeature('services.cloud.commands_interface.send_realtime_metric.input_status.enabled')"
                :device_guid="device.device_guid"
                metric_type="input_status"
                :target="input"
              />
              
              <input-status-label v-else :input="input" />
              
              <input-preview
                v-if="device.isOnline() && isPreviewSupported(device, index) && input.video_input_format != 'FORMAT_UNDEFINED'"
                :device_guid="device.device_guid" 
                :input_id="input.id"
                :input_type="input.type"
              />
            </v-card-text>
          </v-card>
        </v-col>
        
        <v-col cols="4">
          <v-card 
            outlined
            v-for="(encoder, index) in row.encoders['video']" 
            v-bind:key="index"
            class="my-1"
            :class="encoderFailed(encoder) ? errorColor() : ''"
          >
            <v-card-title class="text-subtitle-2 py-2">{{ encoderName(encoder) }} <v-spacer /><v-chip x-small>{{ typeName(encoder.type) }}</v-chip></v-card-title>
            <v-card-text class="my-0 pb-3">
              <encoder-status-label :encoder="encoder" />
            </v-card-text>
          </v-card>
          
          <div 
            v-if="row.encoders['audio']"
          >
            <v-card
              outlined
              v-for="(encoder, index) in row.encoders['audio']" 
              v-bind:key="index"
              class="my-1"
              :class="encoderFailed(encoder) ? errorColor() : ''"
            >
              <v-card-title class="text-subtitle-2 py-2">{{ encoderName(encoder) }} <v-spacer /><v-chip x-small>{{ typeName(encoder.type) }}</v-chip></v-card-title>
              <v-card-text class="my-0 pb-3">
                <encoder-status-label :encoder="encoder" />
              </v-card-text>
            </v-card>
          </div>
          <div 
            v-if="row.encoders['data']"
          >
            <v-card
              outlined
              v-for="(encoder, index) in row.encoders['data']" 
              v-bind:key="index"
              class="my-1"
              :class="encoderFailed(encoder) ? errorColor() : ''"
            >
              <v-card-title class="text-subtitle-2 py-2">{{ encoderName(encoder) }} <v-spacer /><v-chip x-small>{{ typeName(encoder.type) }}</v-chip></v-card-title>
              <v-card-text class="my-0 pb-3">
                <encoder-status-label :encoder="encoder" />
              </v-card-text>
            </v-card>
          </div>
        </v-col>
        
        <v-col cols="4" v-if="row.outputs.length > 0">
          <v-card
            outlined
            v-for="(output, index) in row.outputs" 
            v-bind:key="index"
            class="my-1"
            :class="outputFailed(output) ? errorColor() : ''"
          >
            <v-card-title class="text-subtitle-2 py-2">{{ outputName(output) }} <v-spacer /><v-chip x-small>{{ typeName(output.type) }}</v-chip></v-card-title>
            <v-card-text class="my-0 pb-3">
              {{ (destination=$helpers.outputStatus(output).destination, null) }}
              <output-status-label :output="output" />
              <div v-if="destination" class="mt-1 caption">{{ destination }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="4" class="text-center" align-self="center" v-else>
          <span class="text--disabled">
            No configured outputs
          </span>
        </v-col>
        
        <v-divider 
          v-if="index < rowData.length - 1"
          class="ma-5"
        />
      </v-row>
    </div>
  </div>
</template>
<script>
  import { mapGetters } from 'vuex'

  import InputPreview from './InputPreview.vue'
  import RtMetrics from './RTMetrics.vue'
  
  export default {
    name: 'DeviceMonitorView',
    
    props: ['device'],
    
    components: {
      InputPreview,
      RtMetrics
    },
    
    data() {
      return {
        inputPreviewSupportedVersion: this.$helpers.parseVersion('cloud', 'v1.3.0'),
      }
    },
    computed: {
      ...mapGetters('user', ['user', 'guid', 'isOrgAdmin']),
      
      rowData() {
        var rows = this.device.outputColumnView()
        
        if (rows.length > 0) {
          return rows
        }
        
        return this.device.fallbackColumnView()
      }
    },
    
    mounted() {
      
    },
    
    methods: {
      errorColor() {
        if (this.$vuetify.theme.dark) {
          return 'red darken-4'
        }
        return 'red lighten-5'
      },
      
      itemColor(device) {
        if (this.$vuetify.theme.dark) {
          if (device.streaming_status == 1) {
            return 'green darken-4'
          } else if (device.streaming_status == 0) {
            return 'red darken-4'
          } else if (device.streaming_status == 2) {
            return 'orange darken-4'
          } else if (device.streaming_status == 3) {
            return 'grey darken-4'
          }
        } else {
          if (device.streaming_status == 1) {
            return 'green lighten-5'
          } else if (device.streaming_status == 0) {
            return 'red lighten-5'
          } else if (device.streaming_status == 2) {
            return 'orange lighten-5'
          } else if (device.streaming_status == 3) {
            return 'grey lighten-5'
          }
        }
        
        return 'disabled'
      },
      
      isPreviewSupported(device, index = 0) {
        if (device.thumbnailEnabled(index) && this.$helpers.versionCheck(this.inputPreviewSupportedVersion, device.cloud_version)) {
          return true
        }
        return false
      },
      
      typeName(type) {
        if (!type) {
          return
        }
        return type.replace(/_/g, ' ')
      },
      
      inputName(input) {
        if (input.config.name) {
          return this.$helpers.htmlDecode(input.config.name)
        }
        
        return 'Input #' + input.id
      },
      
      graphicOverlay(input) {
        if (input.config.graphic_overlay && input.config.graphic_overlay.enable) {
          return 'Static Graphic Overlay'
        } else if (input.config.cef && input.config.cef.enable) {
          return 'Dynamic Graphic Overlay'
        }
        return false
      },
      
      encoderName(encoder) {
        if (encoder.config.name) {
          return this.$helpers.htmlDecode(encoder.config.name)
        }
        
        return 'Encoder #' + encoder.id
      },
      
      outputName(output) {
        if (output.config.name) {
          return this.$helpers.htmlDecode(output.config.name)
        }
        
        var outputName = 'Output #' + output.id
        if (output.type) {
          outputName += ' (' + this.$helpers.getLabel(output.type) + ')'
        }
        return outputName
      },
      
      encoderFailed(encoder) {
        if (this.$helpers.encoderStatus(encoder).error === true) {
          return true
        }
        
        return false
      },
      
      outputFailed(output) {
        if (this.$helpers.outputStatus(output).streaming === false && this.$helpers.outputStatus(output).enabled === true) {
          return true
        }
        
        return false
      },
      
      refreshShadow(shadowName) {
        this.$deviceShadows.getDeviceShadow(this.device.device_guid, shadowName).requestShadowUpdate()
      }
    }
  }
</script>